import { useState } from "react";
import { useOffline } from "@/lib/offline/useOffline";
import { WhatsNewLink } from "@/components/WhatsNewLink";
import { BrandLogo } from "@/components/BrandLogo";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, LogOut, MoreHorizontal, BookOpen, Map, GitBranch } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api, errMsg } from "@/lib/api";
import { useInvalidate } from "@/lib/queries";
import { useAuth } from "@/context/AuthContext";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { InvitesBanner } from "@/components/InvitesBanner";
import { NotificationsSection } from "@/components/comments/NotificationsSection";
import { InstallAppSection } from "@/components/InstallAppSection";
import { PlanLinks } from "@/components/billing/PlanLinks";
import { HeaderMenu } from "@/components/HeaderMenu";
import { PartyPopper, ChefHat } from "lucide-react";
import { Toggle } from "@/components/TemplateManager";
import { WORLD_SETTINGS, settingLabel } from "@/lib/templates";

function WorldTypes({ value, onChange }) {
  const [custom, setCustom] = useState("");
  const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  const add = () => {
    const name = custom.trim();
    const builtin = WORLD_SETTINGS.find(([, l]) => l.toLowerCase() === name.toLowerCase())?.[0];
    const k = builtin || `custom:${name}`;
    if (name && !value.includes(k)) onChange([...value, k]);
    setCustom("");
  };
  return (
    <div className="space-y-2" data-testid="universe-world-types">
      <Label>World types <span className="normal-case text-[#A8A090]">· pick every genre that fits</span></Label>
      <div className="flex flex-wrap gap-1.5">
        {WORLD_SETTINGS.map(([k, l]) => <Toggle key={k} on={value.includes(k)} onClick={() => toggle(k)} testid={`new-universe-world-${k}`}>{l}</Toggle>)}
        {value.filter((k) => k.startsWith("custom:")).map((k) => <Toggle key={k} on onClick={() => toggle(k)} testid={`new-universe-world-${k}`}>{settingLabel(k)} ×</Toggle>)}
        <Input value={custom} onChange={(e) => setCustom(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          onBlur={add} placeholder="Custom… (e.g. Solarpunk)" className="h-7 w-48 text-xs" maxLength={40} data-testid="new-universe-world-custom-input" />
      </div>
    </div>
  );
}

function UniverseDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(initial || { name: "", description: "", world_settings: [] });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = initial?.id ? await api.patch(`/universes/${initial.id}`, form) : await api.post("/universes", form);
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="universe-dialog">
        <form onSubmit={save}>
          <DialogHeader><DialogTitle className="font-display text-3xl">{initial?.id ? "Rename universe" : "Found a new universe"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-6">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input data-testid="universe-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="The Shattered Crown" required />
            </div>
            <div className="space-y-2">
              <Label>Premise</Label>
              <Textarea data-testid="universe-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="A fallen empire, three heirs, one cursed throne…" />
            </div>
            {!initial?.id && <WorldTypes value={form.world_settings} onChange={(world_settings) => setForm({ ...form, world_settings })} />}
          </div>
          <DialogFooter><Button type="submit" disabled={busy} data-testid="universe-save-button">{busy ? "Saving…" : "Save"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UniverseCard({ u, i, onEdit, onDelete }) {
  const navigate = useNavigate();
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
      onClick={() => navigate(`/u/${u.id}/notes`)}
      data-testid={`universe-card-${u.id}`}
      className="group relative cursor-pointer rounded-md border border-[#2D3442] bg-[#13171F] p-6 transition-[border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-[#D97706]/60"
    >
      <div className="flex items-start justify-between">
        <div className="lf-label">{u.role}</div>
        {u.role === "owner" && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <button className="text-[#A8A090] hover:text-[#F3EBDD]" data-testid={`universe-menu-${u.id}`}><MoreHorizontal className="h-4 w-4" /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem onClick={() => onEdit(u)} data-testid={`universe-rename-${u.id}`}>Rename</DropdownMenuItem>
              <DropdownMenuItem className="text-red-400" onClick={() => onDelete(u)} data-testid={`universe-delete-${u.id}`}>Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <h3 className="font-display text-3xl mt-4 break-words [overflow-wrap:anywhere] text-[#F3EBDD] group-hover:text-[#F59E0B] transition-colors">{u.name}</h3>
      <p className="mt-2 text-sm text-[#A8A090] line-clamp-2 min-h-[2.5rem]">{u.description || "An unwritten world."}</p>
      <div className="mt-6 flex gap-5 text-xs text-[#A8A090]">
        <span className="flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5" />{u.entity_count} entries</span>
        {u.pro ? (<>
          <span className="flex items-center gap-1.5"><GitBranch className="h-3.5 w-3.5" />{u.timeline_count} timelines</span>
          <span className="flex items-center gap-1.5"><Map className="h-3.5 w-3.5" />{u.map_count} maps</span>
        </>) : <span className="text-[#A8A090]/70" data-testid={`universe-free-${u.id}`}>Free plan</span>}
      </div>
    </motion.div>
  );
}

export default function UniversesPage() {
  const { user, logout } = useAuth();
  const invalidate = useInvalidate();
  const { data: unis = [], isLoading } = useQuery({ queryKey: ["universes"], queryFn: () => api.get("/universes").then((r) => r.data) });
  const [dialog, setDialog] = useState(null);
  const { offline } = useOffline();
  const [toDelete, setToDelete] = useState(null);

  const remove = async () => {
    await api.delete(`/universes/${toDelete.id}`);
    toast.success(`"${toDelete.name}" was unmade`);
    setToDelete(null);
    invalidate("universes");
  };

  return (
    <div className="min-h-screen lf-glow lf-grain">
      <div className="relative z-10 mx-auto max-w-6xl px-6 sm:px-10 py-10">
        <header className="flex items-center justify-between gap-3 mb-20">
          <BrandLogo testId="universes-brand" />
          <div className="sm:hidden"><HeaderMenu user={user} onLogout={logout} /></div>
          <div className="hidden items-center gap-4 text-sm text-[#A8A090] sm:flex">
            <WhatsNewLink />
            <Link to="/library" className="hover:text-[#F3EBDD]" data-testid="more-universes-link">More Universes</Link>
            <Link to="/stats" className="hover:text-[#F3EBDD]" data-testid="your-stats-link">Your stats</Link>
            <Link to="/inspiration" className="hover:text-[#F3EBDD]" data-testid="inspiration-link">Inspiration</Link>
            <Link to="/cookbook" title="Cookbook" aria-label="Cookbook" className="inline-flex items-center hover:text-[#F3EBDD]" data-testid="cookbook-link"><ChefHat className="h-4 w-4" /></Link>
            <Link to="/account#invite" className="hover:text-[#F3EBDD]" data-testid="invite-friends-link">Invite friends</Link>
            <PlanLinks user={user} />
            <span data-testid="current-user-name">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-button"><LogOut className="h-4 w-4 mr-1" />Sign out</Button>
          </div>
        </header>
        {user?.plan?.event && (
          <div className="mb-8 flex items-center gap-3 rounded-md border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-5 py-3 text-sm text-[#FBBF24]" data-testid="event-banner">
            <PartyPopper className="h-4 w-4 shrink-0" />
            <span><span className="font-medium">{user.plan.event.name}:</span> LoreRight Pro is free for everyone until {new Date(user.plan.event.end).toLocaleDateString(undefined, { month: "long", day: "numeric" })}. Happy writing!</span>
          </div>
        )}
        <InvitesBanner />
        <NotificationsSection />
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <div className="lf-label mb-3">Your archive</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F3EBDD] tracking-tight">My Universes</h1>
          </div>
          <Button onClick={() => setDialog({})} disabled={offline} title={offline ? "Creating a universe needs an internet connection" : undefined} data-testid="create-universe-button"><Plus className="h-4 w-4 mr-1" />New universe</Button>
        </div>
        {isLoading ? (
          <div className="text-[#A8A090]">Loading…</div>
        ) : unis.length === 0 ? (
          <div className="rounded-md border border-dashed border-[#2D3442] p-16 text-center" data-testid="universes-empty">
            <p className="font-display text-3xl text-[#F3EBDD]">No worlds yet.</p>
            <p className="mt-2 text-[#A8A090]">Found your first universe and start filling its pages.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="universes-grid">
            {unis.map((u, i) => <UniverseCard key={u.id} u={u} i={i} onEdit={setDialog} onDelete={setToDelete} />)}
          </div>
        )}
        <InstallAppSection />
      </div>
      {dialog && (
        <UniverseDialog open onOpenChange={(o) => !o && setDialog(null)} initial={dialog.id ? dialog : null}
          onSaved={() => { invalidate("universes"); toast.success("Universe saved"); }} />
      )}
      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} onConfirm={remove}
        title="Delete this universe?" description={`"${toDelete?.name}" and all its lore, maps and timelines will be permanently erased.`} />
    </div>
  );
}
